import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moment from 'moment';

import { v4 as uuidv4 } from 'uuid';

import { AppState } from 'src/app/state';
import { uploadReset } from 'src/app/dashboard/state/upload/upload.actions';
import { setSelectedLanguage } from 'src/app/dashboard/state/study/study.actions';
import { createModule, exportModuleToCSV, updateModule } from '../../../../state/module/module.actions';
import {
  selectIsLoadingModule,
  selectModuleLoaded,
  selectSelectedModule,
} from 'src/app/dashboard/state/module/module.selectors';
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/study/study.selectors';
import { Study } from 'src/app/models/study';
import { Module } from 'src/app/models/module';
import { Language } from 'src/app/models/language';
import { StudyType } from '../../../../../models/study-type';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import {
  API_DATE_FORMAT,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  RANDOMIZATION_VALUES,
} from '../../../../../core/dashboard-constants';
import { STUDY_DESCRIPTION_IDENTIFIER, STUDY_TITLE_IDENTIFIER } from '../../study-details/study-details.component';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { isThereEmptyTranslation } from '../../../helpers/translation-helper';
import { importModuleItems } from '../../../../state/module-item/module-item.actions';
import { selectIsImportingModuleItems } from '../../../../state/module-item/module-item.selectors';
import { CategoryListDialogComponent } from '../../../components/category-list-dialog/category-list-dialog.component';

const MODULE_PREFIX_IDENTIFIER = 'prefix';
const SELECT_CSV_BUTTON_LABEL = 'Select CSV';

const SENSOR_PERIODS = [
  { value: 1800000, label: 'Last half an hour(0.5h)' },
  { value: 3600000, label: 'Last hour (1h)' },
  { value: 21600000, label: 'Last 6 hours' },
  { value: 86400000, label: 'Last 24 hours' },
  { value: 604800000, label: 'Last week (7 Days)' },
  { value: 2678400000, label: 'Last month (31 Days)' },
  { value: 0, label: 'Use date-time picker' },
];

@Component({
  selector: 'dashboard-module-form',
  templateUrl: './module-form.component.html',
  styleUrls: ['./module-form.component.scss'],
})
export class ModuleFormComponent implements OnInit, OnDestroy {
  @Input() isEdit: boolean;
  @ViewChild('languageTabs', { static: false }) languageTabs: MatTabGroup;
  @ViewChild('dateStartsPicker') dateStartsPicker: any;
  @ViewChild('dateEndsPicker') dateEndsPicker: any;

  moduleForm: FormGroup;

  now = moment(new Date());

  StudyType = StudyType;

  loadingModule$: Observable<boolean>;
  moduleLoaded$: Observable<boolean>;

  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  selectedModule$: Observable<Module>;
  selectedModule: Module;

  imgPath: string;
  isImageToEdit: boolean;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_MODULE;
  esmModule: boolean;

  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;
  importingModuleItems$: Observable<boolean>;

  SENSOR_PERIODS = SENSOR_PERIODS;
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  SELECT_CSV_BUTTON_LABEL = SELECT_CSV_BUTTON_LABEL;
  RANDOMIZATION_VALUES = RANDOMIZATION_VALUES;

  fileInputValue: string;
  filesToUpload: File[];
  imageUploadUuid: string;

  sleepChecked = false;

  private subscriptions$ = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    public privilegeService: PrivilegeService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  get name(): AbstractControl {
    return this.moduleForm.get('name') as AbstractControl;
  }

  get translations(): FormArray {
    return this.moduleForm.get('translations') as FormArray;
  }

  get imagePath(): AbstractControl {
    return this.moduleForm.get('imagePath') as AbstractControl;
  }

  get progressBar(): AbstractControl {
    return this.moduleForm.get('progressBar') as AbstractControl;
  }

  get randomization(): AbstractControl {
    return this.moduleForm.get('randomization') as AbstractControl;
  }

  get heartRateType(): AbstractControl {
    return this.moduleForm.get('heartRateType') as AbstractControl;
  }

  get stepsType(): AbstractControl {
    return this.moduleForm.get('stepsType') as AbstractControl;
  }

  get sensorDataTimePeriod(): AbstractControl {
    return this.moduleForm.get('sensorDataTimePeriod') as AbstractControl;
  }

  get sleepType(): AbstractControl {
    return this.moduleForm.get('sleepType') as AbstractControl;
  }

  get sensorDataStart(): AbstractControl {
    return this.moduleForm.get('sensorDataStart') as AbstractControl;
  }

  get sensorDataEnd(): AbstractControl {
    return this.moduleForm.get('sensorDataEnd') as AbstractControl;
  }

  get batteryLifeType(): AbstractControl {
    return this.moduleForm.get('batteryLifeType') as AbstractControl;
  }

  get locationType(): AbstractControl {
    return this.moduleForm.get('locationType') as AbstractControl;
  }

  ngOnInit(): void {
    this.moduleForm = this.formBuilder.group({
      name: [
        this.selectedModule?.name ? name : '',
        [Validators.required, Validators.maxLength(100), removeWhitespaces],
      ],
      translations: this.formBuilder.array([]),
      imagePath: [null],
      progressBar: [null],
      randomization: [RANDOMIZATION_VALUES.ROUTING],
      heartRateType: [null],
      sleepType: [null],
      stepsType: [null],
      batteryLifeType: [null],
      locationType: [null],
      sensorDataTimePeriod: [SENSOR_PERIODS[2].value],
      sensorDataStart: [this.now, [Validators.required]],
      sensorDataEnd: [moment(new Date()).add(1, 'minute'), [Validators.required]],
    });

    this.imageUploadUuid = uuidv4();

    this.loadingModule$ = this.store.select(selectIsLoadingModule);
    this.moduleLoaded$ = this.store.select(selectModuleLoaded);

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);
    this.importingModuleItems$ = this.store.select(selectIsImportingModuleItems);

    this.subscriptions$.add(
      combineLatest([this.supportedLanguages$, this.selectedStudy$]).subscribe((data) => {
        this.supportedLanguages = data[0];
        this.selectedStudy = data[1];

        this.removeAllTranslationFields();
        if (this.supportedLanguages?.length) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        } else {
          this.createTranslationFields(this.selectedLanguage.code);
        }

        this.checkPrivileges();
      })
    );

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions$.add(this.selectedLanguage$.subscribe((language) => (this.selectedLanguage = language)));

    this.idsForUpload = {
      studyId: this.selectedStudy?.id,
    };

    this.selectedModule$ = this.store.select(selectSelectedModule);
    this.subscriptions$.add(
      this.selectedModule$.subscribe((module) => {
        this.selectedModule = module;

        if (module) {
          this.esmModule = false;
          if (module.interventionPivots?.length) {
            for (const item of module.interventionPivots) {
              if (item.esmTriggers?.length) {
                this.esmModule = true;
                break;
              }
            }
          }
          this.isEdit = true;
          this.removeAllTranslationFields();
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language.code));
        }

        if (module?.imagePath) {
          this.isImageToEdit = true;
          this.imgPath = module?.imagePath;
        } else {
          this.isImageToEdit = false;
          this.imgPath = null;
          this.store.dispatch(uploadReset({ uuid: this.imageUploadUuid }));
        }

        this.idsForUpload = {
          studyId: module?.studyId,
          moduleId: module?.id,
        };
      })
    );

    this.subscriptions$.add(
      this.sensorDataTimePeriod.valueChanges.subscribe((period) => {
        if (period) {
          this.sensorDataStart.clearValidators();
          this.sensorDataEnd.clearValidators();
        } else {
          this.sensorDataStart.setValidators(Validators.required);
          this.sensorDataEnd.setValidators(Validators.required);
        }
      })
    );
  }

  checkPrivileges(): void {
    if (
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.VIEW) &&
      !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL)
    ) {
      this.moduleForm.disable();
    }
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedModule?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const moduleTitle = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [STUDY_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations?.length ? filteredTranslations[0]?.translation : '',
        [Validators.required, Validators.maxLength(100), removeWhitespaces],
      ],
    });
    const descriptionID = this.selectedStudy?.type === this.StudyType.CC ? 2 : 1;
    const moduleDescription = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[descriptionID]?.id : 0],
      language: [languageCode],
      identifier: [STUDY_DESCRIPTION_IDENTIFIER],
      translation: [filteredTranslations?.length ? filteredTranslations[descriptionID]?.translation : ''],
    });
    const modulePrefix = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [MODULE_PREFIX_IDENTIFIER],
      translation: [filteredTranslations?.length ? filteredTranslations[1]?.translation : ''],
    });

    this.translations.push(moduleTitle);
    if (this.selectedStudy?.type === this.StudyType.CC) {
      this.translations.push(modulePrefix);
    }
    this.translations.push(moduleDescription);
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  openCategoryDialog() {
    this.dialog.open(CategoryListDialogComponent, {
      data: {
        selectedStudy: this.selectedStudy,
        selectedModule: this.selectedModule,
        selectedLanguage: this.selectedLanguage,
      },
      minWidth: '60%',
    });
  }

  saveModule(): void {
    this.moduleForm.markAllAsTouched();

    if (isThereEmptyTranslation(this.translations.value, STUDY_TITLE_IDENTIFIER)) {
      this.toastrService.error('Please populate Title in all selected languages');
      return;
    }

    if (this.moduleForm.invalid) {
      return;
    }

    let moduleDetails = {
      ...this.moduleForm.value,
      studyId: this.selectedStudy?.id,
    };

    if (moduleDetails.sensorDataTimePeriod === 0) {
      moduleDetails.sensorDataStart = moment(this.sensorDataStart.value).format(API_DATE_FORMAT);
      moduleDetails.sensorDataEnd = moment(this.sensorDataEnd.value).format(API_DATE_FORMAT);
    } else {
      moduleDetails.sensorDataStart = null;
      moduleDetails.sensorDataEnd = null;
    }

    if (this.selectedStudy?.type === this.StudyType.CC) {
      delete moduleDetails.imagePath;
      delete moduleDetails.progressBar;
      delete moduleDetails.heartRateType;
      delete moduleDetails.stepsType;
      delete moduleDetails.sleepType;
      delete moduleDetails.randomization;
      delete moduleDetails.locationType;
      delete moduleDetails.batteryLifeType;
    }

    if (this.isEdit) {
      moduleDetails = {
        ...moduleDetails,
        id: this.selectedModule?.id,
      };

      this.store.dispatch(updateModule({ module: moduleDetails }));
      return;
    }

    this.store.dispatch(createModule({ module: moduleDetails }));
  }

  doExportCall() {
    this.store.dispatch(exportModuleToCSV({ module: this.selectedModule }));
  }

  fileChanged(fileInput: any): void {
    const regex = new RegExp('(.*?).(csv)$');

    if (!fileInput.target.files || !fileInput.target.files.length) {
      return;
    }

    this.fileInputValue = fileInput.target.files[0].name.toLowerCase();

    if (!regex.test(this.fileInputValue)) {
      this.toastrService.error('Please select a file with a .csv extension');
      this.fileInputValue = null;
      return;
    }

    this.SELECT_CSV_BUTTON_LABEL = this.fileInputValue.slice(0, 20);

    this.filesToUpload = fileInput.target.files;
  }

  importModuleItems(): void {
    this.store.dispatch(
      importModuleItems({
        moduleFile: this.filesToUpload[0],
        idsForUpload: { studyId: this.selectedModule.studyId, moduleId: this.selectedModule.id },
      })
    );

    this.fileInputValue = null;
    this.filesToUpload = [];
    this.SELECT_CSV_BUTTON_LABEL = SELECT_CSV_BUTTON_LABEL;
  }

  updateSleepChecked() {
    this.sleepChecked = !this.sleepChecked;
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
